import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMockAuth } from '@/hooks/useMockAuth';
import { useTheme } from '@/contexts/ThemeContext';

const LoginPage = () => {
  const { login } = useMockAuth();
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);

  const handleMockLogin = async (userKey: string) => {
    setIsLoading(true);
    try {
      await login(userKey);
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const colors = {
    dark: {
      bg: '#0a0a0f',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.8) 0%, rgba(22, 33, 62, 0.8) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.3)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      inputBg: 'rgba(15, 15, 20, 0.6)',
      inputBorder: 'rgba(102, 126, 234, 0.3)'
    },
    light: {
      bg: '#f9fafb',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      inputBg: '#ffffff',
      inputBorder: '#e5e7eb'
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      minHeight: '89.7vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: theme === 'dark'
        ? 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%)'
        : 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%)',
      padding: '2rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Floating gradient orbs */}
      {theme === 'dark' && (
        <>
          <div style={{
            position: 'absolute',
            top: '-10%',
            right: '-10%',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%)',
            filter: 'blur(60px)',
            animation: 'float 8s ease-in-out infinite'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-10%',
            left: '-10%',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(124, 58, 237, 0.12) 0%, transparent 70%)',
            filter: 'blur(60px)',
            animation: 'float 10s ease-in-out infinite',
            animationDelay: '-5s'
          }} />
        </>
      )}

      <div style={{
        width: '100%',
        maxWidth: '480px',
        background: c.cardBg,
        borderRadius: '1.5rem',
        padding: '3rem',
        border: `1px solid ${c.cardBorder}`,
        backdropFilter: theme === 'dark' ? 'blur(20px)' : 'none',
        boxShadow: theme === 'dark'
          ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Logo/Brand */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            background: theme === 'dark'
              ? 'linear-gradient(135deg, #ffffff 0%, #a8b2ff 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '0.5rem'
          }}>
            patchGrid
          </h1>
          <p style={{
            color: c.textMuted,
            fontSize: '1rem'
          }}>
            Route Your Sound, Your Way
          </p>
        </div>

        {/* Welcome Message */}
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: c.text,
            marginBottom: '0.5rem'
          }}>
            Welcome Back
          </h2>
          <p style={{
            color: c.textMuted,
            fontSize: '0.875rem'
          }}>
            Sign in to access your account and continue building
          </p>
        </div>

        {/* Mock Login Buttons (Dev Mode) */}
        <div style={{
          backgroundColor: theme === 'dark' ? 'rgba(102, 126, 234, 0.1)' : '#f3f4f6',
          border: `1px solid ${theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#e5e7eb'}`,
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem'
          }}>
            <span style={{ fontSize: '1.25rem' }}>🛠️</span>
            <h3 style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: c.text,
              margin: 0,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Development Mode
            </h3>
          </div>
          <p style={{
            fontSize: '0.75rem',
            color: c.textMuted,
            marginBottom: '1rem'
          }}>
            Select a role to log in with mock authentication
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <MockLoginButton
              onClick={() => handleMockLogin('user')}
              disabled={isLoading}
              label="Sign in as User"
              icon="👤"
              color="#2563eb"
              theme={theme}
            />
            <MockLoginButton
              onClick={() => handleMockLogin('creator')}
              disabled={isLoading}
              label="Sign in as Creator"
              icon="🎨"
              color="#7c3aed"
              theme={theme}
            />
            <MockLoginButton
              onClick={() => handleMockLogin('admin')}
              disabled={isLoading}
              label="Sign in as Admin"
              icon="⚡"
              color="#dc2626"
              theme={theme}
            />
          </div>
        </div>

        {/* Production Auth (Commented for Dev) */}
        <div style={{
          padding: '1rem',
          backgroundColor: theme === 'dark' ? 'rgba(124, 58, 237, 0.1)' : '#fef3c7',
          border: `1px solid ${theme === 'dark' ? 'rgba(124, 58, 237, 0.2)' : '#fbbf24'}`,
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          <p style={{
            fontSize: '0.75rem',
            color: theme === 'dark' ? '#c4b5fd' : '#92400e',
            margin: 0,
            textAlign: 'center'
          }}>
            💡 Production authentication with WorkOS will be available soon
          </p>
        </div>

        {/* Footer Links */}
        <div style={{
          textAlign: 'center',
          fontSize: '0.875rem',
          color: c.textMuted
        }}>
          <Link
            to="/"
            style={{
              color: theme === 'dark' ? '#a8b2ff' : '#667eea',
              textDecoration: 'none',
              fontWeight: '500'
            }}
          >
            ← Back to Home
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0);
          }
          33% {
            transform: translate(30px, -30px);
          }
          66% {
            transform: translate(-20px, 20px);
          }
        }
      `}</style>
    </div>
  );
};

interface MockLoginButtonProps {
  onClick: () => void;
  disabled: boolean;
  label: string;
  icon: string;
  color: string;
  theme: 'dark' | 'light';
}

function MockLoginButton({ onClick, disabled, label, icon, color, theme }: MockLoginButtonProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.875rem 1rem',
        backgroundColor: isHovered && !disabled ? color : (theme === 'dark' ? 'rgba(15, 15, 20, 0.6)' : '#ffffff'),
        color: isHovered && !disabled ? 'white' : (theme === 'dark' ? '#ffffff' : '#111827'),
        border: `1px solid ${isHovered && !disabled ? color : (theme === 'dark' ? 'rgba(102, 126, 234, 0.3)' : '#e5e7eb')}`,
        borderRadius: '0.5rem',
        fontSize: '0.875rem',
        fontWeight: '500',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        opacity: disabled ? 0.5 : 1,
        transform: isHovered && !disabled ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: isHovered && !disabled
          ? `0 4px 12px ${color}40`
          : 'none'
      }}
    >
      <span style={{ fontSize: '1.25rem' }}>{icon}</span>
      <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
      {disabled ? (
        <span style={{ fontSize: '0.875rem' }}>⏳</span>
      ) : (
        <span style={{ fontSize: '0.875rem' }}>→</span>
      )}
    </button>
  );
}

export default LoginPage;
